import React from 'react';
import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';
import { SearchInput } from 'ui/standard/input/search_input/search_input';

import {
  SiteFilter,
  SiteFilterType,
} from 'services/pangolin/types';

import {
  getSiteFilterID,
  getSiteFilterLabel,
} from 'ui/history/site_entry/site_entry_logic';

import {
  RemovableSiteEntry,
  SearchSiteEntry,
  SearchSiteEntryProps,
} from 'ui/history/site_entry/site_entry';

import baseStyles from './site_input.module.css';
import { SiteInputStore } from './site_input_store';
import { DomainInputModal } from './domain_input_modal/domain_input_modal';
import { AddressInputModal } from './address_input_modal/address_input_modal';
import { RangeInputModal } from './range_input_modal/range_input_modal';
import { NetworkInputModal } from './network_input_modal/network_input_modal';
import SiteInputMessages from './site_input_messages';

type SiteInputStyles = {
  inputButton: string;
  plusIcon: string;
  placeholder: string;
};

const themedStyles: ThemedStyles<SiteInputStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.daylightInputButton,
    plusIcon: baseStyles.daylightPlusIcon,
    placeholder: baseStyles.daylightPlaceholder,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.midnightInputButton,
    plusIcon: baseStyles.midnightPlusIcon,
    placeholder: baseStyles.midnightPlaceholder,
  },
};


type SiteEntryDisplayProps = {
  store: SiteInputStore;
  entry: SiteFilter;
};

const SiteEntryDisplay = observer(({ store, entry }: SiteEntryDisplayProps) => {
  const onRemoveClick = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.removeEntry(entry);
  }), [store, entry]);

  return <RemovableSiteEntry entry={entry} onRemoveClick={onRemoveClick}/>;
});

type SiteInputDisplayProps = {
  store: SiteInputStore;
  placeholder: string;
};

const SiteInputDisplay = observer(({ store, placeholder }: SiteInputDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.search.modal.showModal();
  }), [store]);
  return (
    <>
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <div className={styles.placeholder}>
          {placeholder}
        </div>
        <div className={styles.plusIcon}/>
      </button>
      {store.entries.map((entry) =>
        <SiteEntryDisplay store={store} entry={entry} key={getSiteFilterID(entry)}/>)}
    </>
  );
});

type SiteInputProps = {
  placeholder: string;
  store: SiteInputStore;
};

@observer export class SiteInput extends React.Component<SiteInputProps> {
  @computed get currentEntryIDs(): Map<string, true> {
    const map: Map<string, true> = new Map();
    const ids = this.props.store.entries.map(getSiteFilterID);
    ids.forEach((id) => map.set(id, true));
    return map;
  }

  filterEntry = (entry: SiteFilter, text: string): boolean => {
    switch (entry.type) {
      case SiteFilterType.DOMAIN:
      case SiteFilterType.ADDRESS:
      case SiteFilterType.RANGE:
      case SiteFilterType.NETWORK: {
        return true;
      }
      default: {
        if (this.currentEntryIDs.has(getSiteFilterID(entry))) {
          return false;
        }
        const trimmed = text.trim().toLocaleLowerCase();
        if (trimmed) {
          const label = getSiteFilterLabel(entry).toLocaleLowerCase();
          return label.indexOf(trimmed) >= 0;
        }
        return true;
      }
    }
  };

  DisplayComponent = () => {
    return <SiteInputDisplay store={this.props.store} placeholder={this.props.placeholder} />;
  };

  ItemComponent = ({ data: entry, ...restProps }: Omit<SearchSiteEntryProps, 'onSelect'>) => {
    const onSelect = React.useCallback(action(() => {
      switch (entry.type) {
        case SiteFilterType.DOMAIN:
          this.props.store.domain.showModal();
          break;
        case SiteFilterType.ADDRESS:
          this.props.store.address.showModal();
          break;
        case SiteFilterType.RANGE:
          this.props.store.range.showModal();
          break;
        case SiteFilterType.NETWORK:
          this.props.store.network.showModal();
          break;
        default:
          this.props.store.commitEntry(entry);
          this.props.store.search.modal.hideModal();
          break;
      }
    }), [this.props.store, entry]);
    return <SearchSiteEntry data={entry} onSelect={onSelect} {...restProps}/>;
  };

  render() {
    return (
      <>
        <SearchInput
          placeholder={this.props.placeholder}
          store={this.props.store.search}
          data={this.props.store.options}
          filterFunc={this.filterEntry}
          keyFunc={getSiteFilterID}
          ItemComponent={this.ItemComponent}
          DisplayComponent={this.DisplayComponent}
        />
        <DomainInputModal
          store={this.props.store.domain}
          onCommit={this.props.store.commitDomain}
          instructions={SiteInputMessages.domain()}
          saveMessage={SiteInputMessages.save()}
          invalidMessage={SiteInputMessages.invalidDomain()}
        />
        <AddressInputModal
          store={this.props.store.address}
          onCommit={this.props.store.commitAddress}
          instructions={SiteInputMessages.address()}
          saveMessage={SiteInputMessages.save()}
          invalidMessage={SiteInputMessages.invalidAddress()}
        />
        <RangeInputModal
          store={this.props.store.range}
          onCommit={this.props.store.commitRange}
          instructions={SiteInputMessages.range()}
          saveMessage={SiteInputMessages.save()}
          invalidMessage={SiteInputMessages.invalidRange()}
        />
        <NetworkInputModal
          store={this.props.store.network}
          onCommit={this.props.store.commitNetwork}
          instructions={SiteInputMessages.network()}
          saveMessage={SiteInputMessages.save()}
          invalidMessage={SiteInputMessages.invalidNetwork()}
        />
      </>
    );
  }
}
